import { useCallback, useMemo, useState } from 'react';

import { useConfig } from '~extension/icon-library/context/Config';
import { useFillColor } from '~extension/icon-library/context/FillColor';
import downloadIcon from '~extension/icon-library/utils/downloadIcon';
import { PlaceholderButton } from '../button/Button';
import { DownloadButton } from '../button/DownloadButton';
import { BackgroundColorPicker } from '../color-picker/BackgroundColorPicker';
import { FillColorPicker } from '../color-picker/FillColorPicker';
import { Dropdown, DropdownOption } from '../Dropdown';

type DownloadPngProps = { icon: Icon };
export const DownloadPng = ({ icon }: DownloadPngProps) => {
  const { pngOutputSizes } = useConfig();
  const { selectedFillColor } = useFillColor();

  const sizes = useMemo(() => {
    return (pngOutputSizes || '0')
      .split(',')
      .map(raw => parseInt(raw.trim(), 10))
      .filter(value => !isNaN(value));
  }, [pngOutputSizes]);

  // Only use the SVG size if the first available size is 1
  const useSvgSize = sizes[0] === 1;

  // set user selected size to first available size
  const [size, setSize] = useState(sizes[0]);

  // prepare size dropdown options
  const sizeOptions = useMemo((): DropdownOption<number>[] => {
    if (useSvgSize) {
      if (icon.width) {
        return [{ value: 1, label: `${icon.width}×${icon.width}` }];
      }
      return [{ value: 1, label: ` ` }];
    } else {
      return sizes.filter(size => size !== 0 && size !== 1).map(size => ({ value: size, label: `${size}×${size}` }));
    }
  }, [icon.width, sizes, useSvgSize]);

  const handleSizeChange = (option: DropdownOption) => {
    setSize(option.value);
  };

  const hasSvg = Boolean(icon.svg);

  const handleDownload = useCallback(() => {
    downloadIcon(icon, { format: 'PNG', size }, selectedFillColor);
  }, [icon, size, selectedFillColor]);

  return (
    <>
      <div className={`border-gray border-solid border-0 border-b flex items-center`}>
        <div className={`box-border flex items-center opacity-50 py-0 px-2 text-left select-none w-1/3`}>Size</div>
        <div className={`flex flex-wrap flex-grow px-0 py-px`}>
          {sizeOptions.length > 1 ? (
            <Dropdown value={size} options={sizeOptions} onChange={handleSizeChange} />
          ) : (
            <div className={`flex items-center justify-end flex-grow h-8 pr-2 text-right select-none`}>
              {sizeOptions[0].label || sizeOptions[0].value}
            </div>
          )}
        </div>
      </div>
      <FillColorPicker disabled={!hasSvg} />
      <BackgroundColorPicker disabled={!hasSvg} />
      {hasSvg ? <DownloadButton label="Download" onClick={handleDownload} /> : <PlaceholderButton />}
    </>
  );
};
